import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChartCard, CompareBar } from "../components/charts";
import { ArrowLeft, Plus, Trash, Flag, Trophy, FilePpt } from "@phosphor-icons/react";
import { toast } from "sonner";

const num = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString());

function SummaryCard({ title, s, tone }) {
  return (
    <div className={`rounded-sm border bg-zinc-950 p-4 ${tone}`}>
      <div className="overline mb-3 text-zinc-400">{title}</div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><div className="text-xs text-zinc-500">Sample Size</div><div className="font-mono font-bold">{s?.count || 0}</div></div>
        <div><div className="text-xs text-zinc-500">Avg Cost / Tyre</div><div className="font-mono font-bold">₹{num(s?.avg_cost)}</div></div>
        <div><div className="text-xs text-zinc-500">Est. Full Life (km)</div><div className="font-mono font-bold">{num(s?.avg_projected_km)} km</div></div>
        <div><div className="text-xs text-zinc-500">Cost / KM</div><div className="font-mono font-bold text-amber-400">₹{num(s?.cpkm)}</div></div>
      </div>
    </div>
  );
}

export default function CompetitionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { meta, hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ is_competitor: false, data_source: "Actual" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/competition/projects/${id}`).then(({ data }) => setData(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addTyre = async () => {
    if (!(form.brand || "").trim()) { toast.error("Brand is required"); return; }
    setSaving(true);
    try {
      await api.post(`/competition/projects/${id}/tyres`, form);
      toast.success("Tyre added");
      setOpen(false); setForm({ is_competitor: false, data_source: "Actual" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const delTyre = async (tid) => {
    try { await api.delete(`/competition/tyres/${tid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Failed to remove"); }
  };

  const [exporting, setExporting] = useState(false);
  const exportPpt = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/competition/projects/${id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(data?.project?.name || "competition")} - Tyre Report.pptx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Presentation downloaded");
    } catch (e) { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  if (!data) return <div className="text-zinc-500">Loading…</div>;
  const p = data.project;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6" data-testid="competition-detail">
      <button data-testid="back-btn" onClick={() => navigate("/competition")} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-amber-500">
        <ArrowLeft size={16} /> Back to Competition
      </button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="overline flex items-center gap-2 text-rose-400"><Flag size={14} weight="fill" /> Competition Project</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{p.name}</h1>
          <div className="mt-1 text-sm text-zinc-500">{p.site_name || "—"}{p.our_brand ? ` · Our brand: ${p.our_brand}` : ""}{p.contact_person ? ` · ${p.contact_person}` : ""}</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportPpt} disabled={exporting} variant="outline" data-testid="export-ppt-btn">
            <FilePpt size={16} className="mr-1" /> {exporting ? "Exporting…" : "Export PPT"}
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="add-tyre-btn" className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
            <Plus size={16} weight="bold" className="mr-1" /> Add Tyre
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SummaryCard title="Our Tyres" s={data.summary?.own} tone="border-emerald-500/30" />
        <SummaryCard title="Competitor Tyres" s={data.summary?.competitor} tone="border-rose-500/30" />
      </div>

      {(data.comparison || []).length > 0 && (
        <div className="space-y-4" data-testid="comparison">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ChartCard title="Cost per KM — projected (₹/km) · lower is better" testid="chart-cpkm">
              <CompareBar data={data.comparison} dataKey="cpkm" prefix="₹" />
            </ChartCard>
            <ChartCard title="Estimated Full Tyre Life (km) · higher is better" testid="chart-life">
              <CompareBar data={data.comparison} dataKey="avg_projected_km" prefix="" suffix=" km" />
            </ChartCard>
          </div>
          <div className="overflow-x-auto rounded-sm border border-zinc-800">
            <table className="w-full min-w-[820px] text-xs" data-testid="comparison-table">
              <thead className="bg-zinc-950 text-left text-zinc-500"><tr>
                <th className="px-3 py-2">Brand</th><th className="px-3 py-2">Side</th><th className="px-3 py-2">Sample</th>
                <th className="px-3 py-2">Avg Cost/Tyre</th><th className="px-3 py-2">Avg Est. Life (km)</th><th className="px-3 py-2">Cost/KM</th>
                <th className="px-3 py-2">Vehicle</th><th className="px-3 py-2">Lead</th><th className="px-3 py-2">Trips/Day</th><th className="px-3 py-2">Haulage</th>
              </tr></thead>
              <tbody className="divide-y divide-zinc-800">
                {data.comparison.map((b) => (
                  <tr key={b.brand} className="bg-zinc-900">
                    <td className="px-3 py-2 font-semibold text-zinc-100">{b.brand}</td>
                    <td className={`px-3 py-2 font-semibold ${b.is_competitor ? "text-rose-400" : "text-emerald-400"}`}>{b.is_competitor ? "Competitor" : "Ours"}</td>
                    <td className="px-3 py-2 tabular-nums">{b.sample_size}</td>
                    <td className="px-3 py-2 font-mono tabular-nums">₹{num(b.avg_cost)}</td>
                    <td className="px-3 py-2 font-mono tabular-nums">{num(b.avg_projected_km)}</td>
                    <td className="px-3 py-2 font-mono tabular-nums text-amber-400">₹{num(b.cpkm)}</td>
                    <td className="px-3 py-2">{b.vehicle_model || "—"}</td>
                    <td className="px-3 py-2 tabular-nums">{b.lead_km != null ? `${b.lead_km} km` : "—"}</td>
                    <td className="px-3 py-2">{b.trips_per_day || "—"}</td>
                    <td className="px-3 py-2">{b.haulage_type || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-zinc-500">Cost/KM = average cost per tyre ÷ average estimated full life (km). Estimated full life extrapolates the km run so far to 100% tread wear (KM run ÷ fraction of tread worn), or uses the manual value you enter.</p>
        </div>
      )}

      <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-5">
        <h2 className="mb-4 flex items-center gap-2 font-display text-sm font-bold tracking-tight text-zinc-200"><Trophy size={16} className="text-amber-400" /> Tyres in this trial ({data.tyres.length})</h2>
        <div className="overflow-x-auto rounded-sm border border-zinc-800">
          <table className="w-full min-w-[820px] text-xs">
            <thead className="bg-zinc-950 text-left text-zinc-500"><tr>
              <th className="px-3 py-2">Side</th><th className="px-3 py-2">Brand</th><th className="px-3 py-2">Serial</th>
              <th className="px-3 py-2">Size</th><th className="px-3 py-2">KM</th><th className="px-3 py-2">Est. Life (km)</th><th className="px-3 py-2">Wear</th><th className="px-3 py-2">Cost</th>
              <th className="px-3 py-2">CPKM (proj)</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody className="divide-y divide-zinc-800">
              {data.tyres.map((t) => (
                <tr key={t.id} className="bg-zinc-900" data-testid={`ctyre-${t.id}`}>
                  <td className={`px-3 py-2 font-semibold ${t.is_competitor ? "text-rose-400" : "text-emerald-400"}`}>{t.is_competitor ? "Comp" : "Ours"}</td>
                  <td className="px-3 py-2">{t.brand}</td>
                  <td className="px-3 py-2 font-mono text-amber-500">{t.serial_number || "—"}</td>
                  <td className="px-3 py-2">{t.size || "—"}</td>
                  <td className="px-3 py-2 font-mono tabular-nums">{num(t.km_delivered_effective)}</td>
                  <td className="px-3 py-2 font-mono tabular-nums">{num(t.projected_km)}</td>
                  <td className="px-3 py-2 font-mono tabular-nums">{t.wear_pct != null ? `${t.wear_pct}%` : "—"}</td>
                  <td className="px-3 py-2 font-mono tabular-nums">₹{num(t.cost)}</td>
                  <td className="px-3 py-2 font-mono tabular-nums text-amber-400">{t.cpkm != null ? `₹${t.cpkm.toLocaleString()}` : "—"}</td>
                  <td className="px-3 py-2">{t.data_source}</td>
                  <td className="px-3 py-2">{t.status}</td>
                  <td className="px-3 py-2">
                    <button data-testid={`del-ctyre-${t.id}`} onClick={() => delTyre(t.id)} className="text-zinc-500 hover:text-rose-400"><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
              {data.tyres.length === 0 && <tr><td colSpan={12} className="px-3 py-8 text-center text-zinc-500">No tyres added yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="ctyre-dialog">
          <DialogHeader><DialogTitle className="font-display">Add Tyre to Trial</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Side</Label>
              <Select value={form.is_competitor ? "comp" : "ours"} onValueChange={(v) => set("is_competitor", v === "comp")}>
                <SelectTrigger data-testid="ctyre-side" className="mt-1 bg-zinc-950"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ours">Our Tyre</SelectItem>
                  <SelectItem value="comp">Competitor Tyre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-zinc-400">Brand *</Label>
              <Input data-testid="ctyre-brand" value={form.brand || ""} onChange={(e) => set("brand", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Pattern / Model</Label>
              <Input value={form.pattern || ""} onChange={(e) => set("pattern", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Serial Number</Label>
              <Input value={form.serial_number || ""} onChange={(e) => set("serial_number", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Size</Label>
              <Select value={form.size || ""} onValueChange={(v) => set("size", v)}>
                <SelectTrigger className="mt-1 bg-zinc-950"><SelectValue placeholder="Size" /></SelectTrigger>
                <SelectContent>{(meta?.tyre_sizes || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs text-zinc-400">Vehicle</Label>
              <Input data-testid="ctyre-vehicle" value={form.vehicle_label || ""} placeholder="e.g. HD-07" onChange={(e) => set("vehicle_label", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Position</Label>
              <Input data-testid="ctyre-position" value={form.position || ""} placeholder="e.g. 1FL" onChange={(e) => set("position", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Cost (₹)</Label>
              <Input data-testid="ctyre-cost" type="number" value={form.cost ?? ""} onChange={(e) => set("cost", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Fitment KM</Label>
              <Input type="number" value={form.fitment_km ?? ""} onChange={(e) => set("fitment_km", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Current KM (as on date)</Label>
              <Input data-testid="ctyre-currentkm" type="number" value={form.current_km ?? ""} onChange={(e) => set("current_km", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Removal KM (if removed)</Label>
              <Input type="number" value={form.removal_km ?? ""} onChange={(e) => set("removal_km", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">KM Delivered (manual override)</Label>
              <Input data-testid="ctyre-km" type="number" value={form.km_delivered ?? ""} onChange={(e) => set("km_delivered", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div className="sm:col-span-2 mt-1 rounded-sm border border-amber-500/20 bg-zinc-950/60 p-3">
              <div className="overline mb-1 text-amber-400">Tyre Life Projection — estimated km at 100% wear</div>
              <p className="mb-3 text-xs text-zinc-500">Enter tread readings. We estimate how far the tyre runs when fully worn:&nbsp;
                <span className="text-zinc-300">Est. Full Life = KM run ÷ fraction of tread worn</span>.
                <br />Example: 8,000 km run, tread 50→40 mm (20% worn) → <span className="text-amber-400">40,000 km</span> estimated full life.</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><Label className="text-xs text-zinc-400">Initial Tread (mm)</Label>
                  <Input data-testid="ctyre-inittread" type="number" placeholder="e.g. 50" value={form.initial_tread ?? ""} onChange={(e) => set("initial_tread", e.target.value)} className="mt-1 bg-zinc-950" /></div>
                <div><Label className="text-xs text-zinc-400">Current Tread (mm)</Label>
                  <Input data-testid="ctyre-curtread" type="number" placeholder="e.g. 40" value={form.current_tread ?? ""} onChange={(e) => set("current_tread", e.target.value)} className="mt-1 bg-zinc-950" /></div>
                <div><Label className="text-xs text-zinc-400">Est. Full Life km (manual override)</Label>
                  <Input type="number" placeholder="optional — overrides the estimate" value={form.projected_life_km ?? ""} onChange={(e) => set("projected_life_km", e.target.value)} className="mt-1 bg-zinc-950" /></div>
                <div><Label className="text-xs text-zinc-400">% Life Reached (use only if no tread)</Label>
                  <Input data-testid="ctyre-projpct" type="number" placeholder="e.g. 80" value={form.projection_pct ?? ""} onChange={(e) => set("projection_pct", e.target.value)} className="mt-1 bg-zinc-950" /></div>
              </div>
            </div>
            <div><Label className="text-xs text-zinc-400">Vehicle Model</Label>
              <Input value={form.vehicle_model || ""} placeholder="TATA 3525K" onChange={(e) => set("vehicle_model", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Lead (km)</Label>
              <Input type="number" value={form.lead_km ?? ""} onChange={(e) => set("lead_km", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Trips / Day</Label>
              <Input value={form.trips_per_day || ""} placeholder="15-17" onChange={(e) => set("trips_per_day", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Load (tons)</Label>
              <Input type="number" value={form.load_tons ?? ""} onChange={(e) => set("load_tons", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Haulage Type</Label>
              <Input value={form.haulage_type || ""} placeholder="Off Road (rocky)" onChange={(e) => set("haulage_type", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Data Source</Label>
              <Select value={form.data_source || "Actual"} onValueChange={(v) => set("data_source", v)}>
                <SelectTrigger data-testid="ctyre-source" className="mt-1 bg-zinc-950"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Actual">Actual</SelectItem><SelectItem value="Estimated">Estimated</SelectItem></SelectContent>
              </Select></div>
            <div><Label className="text-xs text-zinc-400">Status</Label>
              <Input value={form.status || ""} placeholder="Running / Removed / Scrap" onChange={(e) => set("status", e.target.value)} className="mt-1 bg-zinc-950" /></div>
            <div className="sm:col-span-2"><Label className="text-xs text-zinc-400">Notes</Label>
              <Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="mt-1 bg-zinc-950" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={addTyre} disabled={saving} data-testid="ctyre-save" className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">{saving ? "Adding…" : "Add Tyre"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
